import classNames from 'classnames'
import { InputHTMLAttributes, ReactElement, useState } from 'react'
import Icon from './Icon'

type RadioButtonOption = {
  groupName: string
  value: string
  label: string
  labelNode?: ReactElement
}

export default function RadioButton({
  checkedValue,
  option,
  radioClassName,
  labelProps,
  onChange,
  inputProps,
  labelPosition = 'leading',
}: {
  checkedValue?: string
  option: RadioButtonOption
  labelPosition?: 'leading' | 'trailing'
  radioClassName?: string
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  onChange: (value: string) => void
  inputProps?: InputHTMLAttributes<HTMLInputElement>
}) {
  const checked = checkedValue === option.value
  const disabled = inputProps?.disabled
  return (
    <>
      <div className="flex babu-text-paragraph mb-m">
        <label
          htmlFor={option.label}
          {...labelProps}
          className={classNames(
            'flex items-center justify-between w-full cursor-pointer transition-colors duration-300',
            labelPosition === 'leading' ? 'flex-row' : 'flex-row-reverse',
            checked ? 'text-text600' : 'text-text500',
            !disabled && 'hover:text-text600',
            labelProps?.className,
          )}>
          {option.labelNode || option.label}
          <input
            type="radio"
            name={option.groupName}
            id={option.label}
            value={option.value}
            onChange={e => onChange(e.target.value)}
            {...inputProps}
            className={classNames('hidden', inputProps?.className)}
          />
          <span
            className={classNames(
              'w-6 h-6 inline-block box-border rounded-full border border-solid ml-2 mr-4 relative',
              'transition-colors duration-300',
              checked && 'border-primary',
              disabled && 'border-bg300',
              radioClassName,
            )}>
            <i
              className={classNames(
                'inline-block absolute top-1.5 left-1.5 w-2.5 h-2.5 rounded-full transition-colors duration-300',
                checked ? 'bg-primary' : 'bg-transparent',
              )}
            />
          </span>
        </label>
      </div>
    </>
  )
}

// mock data (Usage for RadioButton component)
const mockPaymentMethodOptions: RadioButtonOption[] = [
  {
    groupName: 'payment_method', // radio buttons group
    value: 'credit',
    label: '信用卡',
    labelNode: (
      <div>
        <Icon name="wallet" />
        <span className="cursor-pointer whitespace-nowrap ml-xs">信用卡</span>
      </div>
    ),
  },
  {
    groupName: 'payment_method',
    value: 'atm',
    label: 'ATM 轉帳（尚未開放）',
    labelNode: (
      <div>
        <Icon name="money" />
        <span className="cursor-pointer whitespace-nowrap ml-xs">
          ATM 轉帳（尚未開放）
        </span>
      </div>
    ),
  },
]

export function ExampleRadioButtonGroup() {
  const [radioValue, setRadioValue] = useState('')

  const handleButtonOnChange = (value: string) => {
    console.log('*** radioOnChange >> value', value)
    setRadioValue(value)
  }
  return (
    <>
      {mockPaymentMethodOptions.map((option, index) => (
        <RadioButton
          key={index}
          option={option}
          checkedValue={radioValue}
          onChange={handleButtonOnChange}
        />
      ))}
    </>
  )
}
